import { useState, useRef, useEffect, forwardRef, useImperativeHandle } from 'react'
import { useEditor, EditorContent } from '@tiptap/react'
import StarterKit from '@tiptap/starter-kit'
import { toast } from 'sonner'
import {
  ArrowLeft,
  Save,
  Check,
  AlertCircle,
  Printer,
  FileText,
  LayoutGrid,
  Eye,
  Settings2,
  ChevronDown,
  ChevronUp,
} from 'lucide-react'
import { Card } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import Toolbar from './Toolbar'
import DocumentList from './DocumentList'
import PagedEditor from './PagedEditor'
import DocumentMetadata from './DocumentMetadata'
import PDFPreviewModal from './PDFPreviewModal'
import { FlightItinerary } from './extensions/FlightItinerary'
import { PageBreak } from './extensions/PageBreak'
import { AiHighlight } from './extensions/AiHighlight'
import { documentsAPI } from '@/utils/api'
import './styles.css'

// Translations for German/English
const translations = {
  de: {
    back: 'Zurück',
    titlePlaceholder: 'Dokumenttitel',
    newDocumentTitle: 'Neues Dokument',
    startContent: '<p>Beginnen Sie hier mit dem Schreiben...</p>',
    standard: 'Standard',
    paged: 'Seiten',
    documentSettings: 'Dokumenteinstellungen',
    savedSuccess: 'Erfolgreich gespeichert',
    unsavedChanges: 'Ungespeicherte Änderungen',
    pdfPreview: 'PDF Vorschau',
    printPreview: 'Druckvorschau',
    saving: 'Speichert...',
    save: 'Speichern',
    loading: 'Dokument wird geladen...',
    saveFirstPdf: 'Bitte speichern Sie das Dokument zuerst, bevor Sie die PDF-Vorschau öffnen',
    saveFirstPrint: 'Bitte speichern Sie das Dokument zuerst, bevor Sie drucken',
    enterTitle: 'Bitte geben Sie einen Dokumenttitel ein',
    editorNotInit: 'Editor nicht initialisiert',
    loadError: 'Fehler beim Laden des Dokuments',
    saveError: 'Fehler beim Speichern des Dokuments',
    saveSuccess: (title) => `Dokument "${title}" wurde gespeichert`,
    unsavedConfirm: 'Sie haben ungespeicherte Änderungen. Möchten Sie wirklich zurück?',
  },
  en: {
    back: 'Back',
    titlePlaceholder: 'Document title',
    newDocumentTitle: 'Untitled Document',
    startContent: '<p>Start typing your document...</p>',
    standard: 'Standard',
    paged: 'Paged',
    documentSettings: 'Document Settings',
    savedSuccess: 'Saved successfully',
    unsavedChanges: 'Unsaved changes',
    pdfPreview: 'PDF Preview',
    printPreview: 'Print Preview',
    saving: 'Saving...',
    save: 'Save Document',
    loading: 'Loading document...',
    saveFirstPdf: 'Please save the document first before previewing PDF',
    saveFirstPrint: 'Please save the document first before printing',
    enterTitle: 'Please enter a document title',
    editorNotInit: 'Editor not initialized',
    loadError: 'Failed to load document',
    saveError: 'Failed to save document',
    saveSuccess: (title) => `Document "${title}" has been saved`,
    unsavedConfirm: 'You have unsaved changes. Are you sure you want to go back?',
  },
}

/**
 * DocumentEditor - Full-featured legal document editor
 *
 * @param {number} caseId - Optional case ID to filter documents by case
 * @param {string} locale - Language locale ('de' or 'en'), defaults to 'de'
 * @param {boolean} embedded - If true, renders without outer Card/container styling
 * @param {React.Ref} ref - Ref to expose methods for programmatic control
 */
const DocumentEditor = forwardRef(function DocumentEditor({
  caseId = null,
  locale = 'de',
  embedded = false,
  // Legacy props (kept for backwards compatibility)
  wsStatus,
  activeTab,
  onNavigateHome,
  onTabChange,
}, ref) {
  const t = translations[locale] || translations.de
  const [view, setView] = useState('list') // 'list' or 'editor'
  const [currentDocument, setCurrentDocument] = useState(null)
  const [documentTitle, setDocumentTitle] = useState('')
  const [saving, setSaving] = useState(false)
  const [loading, setLoading] = useState(false)
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false)
  const [showSavedIndicator, setShowSavedIndicator] = useState(false)
  const [editorMode, setEditorMode] = useState('standard') // 'standard' or 'paged'

  // Document metadata state
  const [showMetadata, setShowMetadata] = useState(false)
  const [metadata, setMetadata] = useState({
    template: null,
    document_date: null,
    case_reference: '',
    recipient_name: '',
    recipient_address: '',
  })

  // PDF preview modal state
  const [showPdfPreview, setShowPdfPreview] = useState(false)

  // Ref to track when we're loading content programmatically (not user edits)
  const isLoadingContentRef = useRef(false)

  // Ref to store pending content when loadGeneratedContent is called before editor is ready
  const pendingContentRef = useRef(null)

  // SINGLE EDITOR INSTANCE - source of truth for both modes
  const editor = useEditor({
    extensions: [
      StarterKit,
      FlightItinerary,
      PageBreak,
      AiHighlight,
    ],
    content: t.startContent,
    editorProps: {
      attributes: {
        class: 'tiptap-editor',
      },
    },
    onUpdate: () => {
      // Only mark as unsaved if this is a real user edit (not programmatic content load)
      if (!isLoadingContentRef.current) {
        setHasUnsavedChanges(true)
        setShowSavedIndicator(false) // Clear saved indicator when editing
      }
    },
  })

  // Expose methods via ref for programmatic control (e.g., from document generation)
  useImperativeHandle(ref, () => ({
    loadGeneratedContent(contentJson, title) {
      setCurrentDocument(null)
      setDocumentTitle(title)
      setMetadata({
        template: null,
        document_date: new Date().toISOString().split('T')[0],
        case_reference: '',
        recipient_name: '',
        recipient_address: '',
      })

      // Load content immediately if editor is ready, otherwise store for later
      if (editor) {
        isLoadingContentRef.current = true
        editor.commands.setContent(contentJson)
        setTimeout(() => {
          isLoadingContentRef.current = false
        }, 0)
      } else {
        // Store content for when editor initializes
        pendingContentRef.current = contentJson
      }

      setHasUnsavedChanges(true) // Mark as unsaved so user knows to save
      setView('editor')
    }
  }), [editor])

  // Load pending content when editor becomes ready
  useEffect(() => {
    if (editor && pendingContentRef.current) {
      isLoadingContentRef.current = true
      editor.commands.setContent(pendingContentRef.current)
      pendingContentRef.current = null
      setTimeout(() => {
        isLoadingContentRef.current = false
      }, 0)
    }
  }, [editor])

  // Switch back to standard mode for editing
  const handleEditInStandardMode = () => {
    setEditorMode('standard')
  }

  // Load document into editor
  const loadDocument = async (document) => {
    setLoading(true)
    setCurrentDocument(document)
    setDocumentTitle(document.title)
    setView('editor')
    setHasUnsavedChanges(false)

    try {
      // Fetch full document with content_json using centralized API
      const data = await documentsAPI.getDocument(document.id)
      if (data.status === 'success' && data.data) {
        setCurrentDocument(data.data)

        // Update metadata from loaded document
        setMetadata({
          template: data.data.template || null,
          document_date: data.data.document_date || null,
          case_reference: data.data.case_reference || '',
          recipient_name: data.data.recipient_name || '',
          recipient_address: data.data.recipient_address || '',
        })

        // Update editor content if editor is ready
        if (editor && data.data.content_json) {
          isLoadingContentRef.current = true
          editor.commands.setContent(data.data.content_json)
          // Use setTimeout to reset flag after setContent completes
          setTimeout(() => {
            isLoadingContentRef.current = false
          }, 0)
        }
      } else {
        throw new Error(data.message || 'Failed to load document')
      }
    } catch (err) {
      console.error('Error loading document:', err)
      toast.error(t.loadError)
    } finally {
      setLoading(false)
    }
  }

  // Create new document
  const createNewDocument = () => {
    setCurrentDocument(null)
    setDocumentTitle(t.newDocumentTitle)
    // Reset metadata for new document
    setMetadata({
      template: null,
      document_date: new Date().toISOString().split('T')[0], // Default to today
      case_reference: '',
      recipient_name: '',
      recipient_address: '',
    })
    if (editor) {
      isLoadingContentRef.current = true
      editor.commands.setContent(t.startContent)
      setTimeout(() => {
        isLoadingContentRef.current = false
      }, 0)
    }
    setHasUnsavedChanges(false)
    setView('editor')
  }

  // Back to list
  const backToList = () => {
    if (hasUnsavedChanges) {
      if (confirm(t.unsavedConfirm)) {
        setView('list')
        setHasUnsavedChanges(false)
      }
    } else {
      setView('list')
    }
  }

  // Handle metadata change (marks as unsaved)
  const handleMetadataChange = (newMetadata) => {
    setMetadata(newMetadata)
    setHasUnsavedChanges(true)
    setShowSavedIndicator(false)
  }

  // Open PDF preview modal (auto-saves new documents or unsaved changes)
  const openPdfPreview = async () => {
    // Auto-save if new document OR has unsaved changes
    if (!currentDocument || !currentDocument.id || hasUnsavedChanges) {
      const saved = await saveDocument()
      if (!saved) return // Don't open modal if save failed
    }

    setShowPdfPreview(true)
  }

  // Save document
  const saveDocument = async () => {
    if (!documentTitle.trim()) {
      toast.warning(t.enterTitle)
      return false
    }

    if (!editor) {
      toast.error(t.editorNotInit)
      return false
    }

    try {
      setSaving(true)
      // Always get content from the single editor instance
      const contentJson = editor.getJSON()

      const payload = {
        title: documentTitle.trim(),
        content_json: contentJson,
        // Include metadata fields
        template: metadata.template,
        document_date: metadata.document_date,
        case_reference: metadata.case_reference,
        recipient_name: metadata.recipient_name,
        recipient_address: metadata.recipient_address,
        // Include case if provided
        ...(caseId && { case: caseId }),
      }

      let data
      if (currentDocument && currentDocument.id) {
        // Update existing document
        data = await documentsAPI.updateDocument(currentDocument.id, payload)
      } else {
        // Create new document
        data = await documentsAPI.createDocument(payload)
      }

      if (data.status === 'success') {
        setCurrentDocument(data.data)
        setHasUnsavedChanges(false)

        // Show success notification
        toast.success(t.saveSuccess(documentTitle))

        // Show "Saved" indicator and auto-hide after 3 seconds
        setShowSavedIndicator(true)
        setTimeout(() => setShowSavedIndicator(false), 3000)
        return true
      } else {
        throw new Error(data.message || 'Failed to save document')
      }
    } catch (err) {
      console.error('Error saving document:', err)
      toast.error(`Failed to save document: ${err.message}`)
      return false
    } finally {
      setSaving(false)
    }
  }

  // Open print preview in new tab
  const openPrintPreview = () => {
    if (!currentDocument || !currentDocument.id) {
      toast.warning(t.saveFirstPrint)
      return
    }

    // Store document data in sessionStorage for print preview
    const documentData = {
      ...currentDocument,
      title: documentTitle, // Use current title (might be edited)
      content_json: editor ? editor.getJSON() : currentDocument.content_json,
    }
    sessionStorage.setItem('printPreviewDocument', JSON.stringify(documentData))

    // Open print preview in new tab
    window.open('/print-preview', '_blank')
  }

  // Content wrapper - conditionally applies outer styling
  const content = (
    <>
      {view === 'list' ? (
        <DocumentList
          onEditDocument={loadDocument}
          onNewDocument={createNewDocument}
          caseId={caseId}
          locale={locale}
        />
      ) : (
        <div className="space-y-4">
          {/* Editor Header with Title and Actions */}
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div className="flex items-center gap-3 flex-1 w-full sm:w-auto">
              <Button
                variant="ghost"
                onClick={backToList}
                className="gap-1"
              >
                <ArrowLeft className="h-4 w-4" />
                {t.back}
              </Button>
              <Input
                value={documentTitle}
                onChange={(e) => {
                  setDocumentTitle(e.target.value)
                  setHasUnsavedChanges(true)
                  setShowSavedIndicator(false)
                }}
                placeholder={t.titlePlaceholder}
                className="flex-1 max-w-[400px] text-lg font-semibold"
              />
            </div>
            <div className="flex items-center gap-2">
              {/* Editor Mode Toggle */}
              <div className="flex border rounded-lg overflow-hidden">
                <button
                  className={`px-3 py-1.5 text-sm flex items-center gap-1.5 transition-colors ${
                    editorMode === 'standard'
                      ? 'bg-sidebar-active text-action-text'
                      : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                  }`}
                  onClick={() => setEditorMode('standard')}
                >
                  <FileText className="h-4 w-4" />
                  {t.standard}
                </button>
                <button
                  className={`px-3 py-1.5 text-sm flex items-center gap-1.5 transition-colors ${
                    editorMode === 'paged'
                      ? 'bg-sidebar-active text-action-text'
                      : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                  }`}
                  onClick={() => setEditorMode('paged')}
                >
                  <LayoutGrid className="h-4 w-4" />
                  {t.paged}
                </button>
              </div>
            </div>
          </div>

          {/* Action Buttons Row */}
          <div className="flex items-center justify-between gap-3">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowMetadata(!showMetadata)}
              className="gap-1 text-slate-600"
            >
              <Settings2 className="h-4 w-4" />
              {t.documentSettings}
              {showMetadata ? (
                <ChevronUp className="h-4 w-4" />
              ) : (
                <ChevronDown className="h-4 w-4" />
              )}
            </Button>

            <div className="flex items-center gap-3">
              {showSavedIndicator && !hasUnsavedChanges && (
                <span className="text-sm text-green-600 font-medium flex items-center gap-1">
                  <Check className="h-4 w-4" />
                  {t.savedSuccess}
                </span>
              )}
              {hasUnsavedChanges && !showSavedIndicator && (
                <span className="text-sm text-amber-600 font-medium">
                  {t.unsavedChanges}
                </span>
              )}
              <Button
                variant="outline"
                onClick={openPdfPreview}
                className="gap-1"
              >
                <Eye className="h-4 w-4" />
                {t.pdfPreview}
              </Button>
              <Button
                variant="outline"
                onClick={openPrintPreview}
                className="gap-1"
              >
                <Printer className="h-4 w-4" />
                {t.printPreview}
              </Button>
              <Button
                onClick={saveDocument}
                disabled={!documentTitle.trim() || !hasUnsavedChanges || saving}
                className="gap-1"
              >
                <Save className="h-4 w-4" />
                {saving ? t.saving : t.save}
              </Button>
            </div>
          </div>

          {/* Collapsible Metadata Section */}
          {showMetadata && (
            <DocumentMetadata
              metadata={metadata}
              onChange={handleMetadataChange}
              disabled={loading}
            />
          )}

          {/* Loading Indicator */}
          {loading && (
            <div className="text-center py-10">
              <p className="text-slate-500 text-lg">{t.loading}</p>
            </div>
          )}

          {/* Toolbar and Editor */}
          {!loading && editorMode === 'standard' && (
            <>
              <Toolbar editor={editor} />
              <EditorContent editor={editor} />
            </>
          )}

          {/* Paged Editor - Preview Only */}
          {!loading && editorMode === 'paged' && editor && (
            <PagedEditor
              content={editor.getJSON()}
              pageFormat="A4"
              showPageNumbers={true}
              header={documentTitle || "Document"}
              footer={null}
              headerHeight={60}
              footerHeight={60}
              onEditClick={handleEditInStandardMode}
            />
          )}
        </div>
      )}

      {/* PDF Preview Modal */}
      <PDFPreviewModal
        isOpen={showPdfPreview}
        onClose={() => setShowPdfPreview(false)}
        documentId={currentDocument?.id}
        documentTitle={documentTitle}
      />
    </>
  )

  // If embedded, render without outer styling
  if (embedded) {
    return content
  }

  // Default: render with full page styling
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
      <div className="max-w-6xl mx-auto py-6 px-4">
        <Card className="p-6 border-0 shadow-lg bg-white">
          {content}
        </Card>
      </div>
    </div>
  )
})

export default DocumentEditor
